import { useState, useEffect, useRef } from "react";
import {
  Discipline,
  StartnumberRfidTag,
  StartNumberSequence,
} from "./api/Dtos";
import { useGlobalContext } from "./Layout/GlobalState";
import {
  ChakraProvider,
  HStack,
  Box,
  Button,
  Input,
  Text,
  Select,
  VStack,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  TableContainer,
  Tfoot,
  Tag,
  Center,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
} from "@chakra-ui/react";
import { useStartnumberRfidTagService } from "./Services/StartnumberRfidTagsService";
import { FastTable } from "./Table/FastTable";
import { DELETE } from "./api/BackendApi";

export function RegisterBibs() {
  const [error, setError] = useState<string>("");

  const globalContext = useGlobalContext();
  const disciplines = globalContext.competition?.disciplines ?? [];

  const [selectedDiscipline, setSelectedDiscipline] =
    useState<Discipline | null>(null);

  const [selectedSequence, setSelectedSequence] =
    useState<StartNumberSequence | null>(null);

  const [isFetching, setIsFetching] = useState(false);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);

  const [currentStartnr, setCurrentStartnr] = useState<number | null>(null);
  const rfidTagService = useStartnumberRfidTagService(currentStartnr);
  //  TODO: Continue with the following
  // Adjust this code to fit with the new useStartnumberRfidTagService
  // overlap between conflict and found tags
  //    same with the found many times one...
  //  can get more tags than we need.
  //  should prompt for retry if conflict found (or deletion, or modification)
  //  should only push as many tags as we need, not more
  //  do not push if conflicts still...
  //...
  const [tick, setTick] = useState(0);

  const [tagsPerStartnumber, setTagsPerStartnumber] = useState(2);
  const [numberOfAntennasInUse, setNumberOfAntennasInUse] = useState(2);

  const startFetching = () => {
    if (!isFetching) {
      setIsFetching(true);
      const id = setInterval(async () => {
        setTick((old) => old + 1);
      }, 1000); // Hämta varje sekund
      setIntervalId(id);
    }
  };

  const stopFetching = () => {
    if (isFetching && intervalId) {
      clearInterval(intervalId);
      setIsFetching(false);
      setIntervalId(null);
    }
  };

  // Trigger on every tick from the "fetch interval"
  useEffect(() => {
    if (
      intervalId == null ||
      currentStartnr == null ||
      selectedSequence?.lastStartNumber == null
    ) {
      return; // Do nothing as all is not configured yet
    }

    const status = getTagCollectionStatus(
      rfidTagService.startnumber.detectedTags,
      rfidTagService.startnumber.detectedConflicts,
      rfidTagService.db.allAntennas,
      tagsPerStartnumber,
      numberOfAntennasInUse
    );

    if (
      status.status === TagCollectionStatus.MISSING_ANTENNA_OBSERVATIONS ||
      status.status === TagCollectionStatus.TOO_FEW_TAGS ||
      status.status === TagCollectionStatus.NO_TAGS_DETECTED
    ) {
      // Fetch more tags
      rfidTagService.functions.fetch(
        tagsPerStartnumber,
        numberOfAntennasInUse,
        [...rfidTagService.db.allAntennas]
      );
    } else if (status.status === TagCollectionStatus.TAG_COLLECTION_COMPLETE) {
      // Save new entries and continue to next startnumber
      rfidTagService.functions.saveNewTags().finally(() => {
        if (currentStartnr >= selectedSequence.lastStartNumber) {
          clearInterval(intervalId);
          setIntervalId(null);
          setIsFetching(false);
          setCurrentStartnr(null);
          return;
        }
        setCurrentStartnr((old) => (old != null ? old + 1 : null));
      });
    }
  }, [tick]);

  if (error.length > 0 && !error.startsWith("Ange"))
    return <div>Fel: {error}</div>;

  return (
    <div>
      <HStack
        padding={"10px"}
        spacing={4}
        justify={"space-evenly"}
        marginBottom={"10px"}
      >
        <ChakraProvider>
          <Box width="100%" fontSize="48px">
            Registrera taggar
          </Box>
        </ChakraProvider>

        <HStack
          padding={"10px"}
          margin="10px"
          borderRadius={"10px"}
          backgroundColor={"gray.600"}
          textColor={"white"}
          fontWeight={"bold"}
          borderColor={"white"}
        >
          <HStack marginLeft={"30px"}>
            <Text>Taggar per nummerlapp</Text>
            <NumberInput
              defaultValue={2}
              min={1}
              max={9}
              onChange={(valueString, valueNumber) =>
                setTagsPerStartnumber(valueNumber)
              }
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper color={"white"} />
                <NumberDecrementStepper color={"white"} />
              </NumberInputStepper>
            </NumberInput>
          </HStack>

          <HStack marginLeft={"30px"}>
            <Text>Antal antenner</Text>
            <NumberInput
              defaultValue={2}
              min={1}
              max={9}
              onChange={(valueString, valueNumber) =>
                setNumberOfAntennasInUse(valueNumber)
              }
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper color="white" />
                <NumberDecrementStepper color="white" />
              </NumberInputStepper>
            </NumberInput>
          </HStack>
        </HStack>
      </HStack>

      <HStack spacing={4} marginBottom={"20px"}>
        <Box>
          <Text fontSize={"16px"}>Välj klass:</Text>
          <Select
            placeholder="Välj klass"
            size={"lg"}
            fontSize={"16px"}
            width={"fit-content"}
            bg="gray.200"
            onChange={(selected) => {
              console.log("Selected discipline:", selected.currentTarget.value);
              const disciplineId = selected.currentTarget.value;
              const selectedDisciplines = disciplines.filter(
                (d) => `${d.id}` === disciplineId
              );
              if (selectedDisciplines && selectedDisciplines.length > 0) {
                const discipline = selectedDisciplines[0];
                setSelectedDiscipline(discipline);
              } else {
                setSelectedDiscipline(null);
              }
              setSelectedSequence(null);
            }}
          >
            {disciplines.map((d) => {
              return (
                <option key={"discipline_" + d.name} value={d.id}>
                  {d.name}
                </option>
              );
            })}
          </Select>
        </Box>

        {selectedDiscipline &&
          selectedDiscipline.startNumberSequences.length > 0 && (
            <Box>
              <Text fontSize={"18px"}>Välj startnummerintervall:</Text>
              <Select
                placeholder="Välj startnummerintervall"
                size={"lg"}
                fontSize={"18px"}
                width={"fit-content"}
                bg="gray.200"
                value={startnumberSequenceToString(selectedSequence)}
                onChange={(selected) => {
                  const selectedValue = selected.currentTarget.value;
                  const selectedSequences =
                    selectedDiscipline.startNumberSequences.filter(
                      (sns) =>
                        startnumberSequenceToString(sns) === selectedValue
                    );
                  if (selectedSequences.length > 0) {
                    console.log(
                      "Selected start number sequence:",
                      selectedValue
                    );
                    setSelectedSequence(selectedSequences[0]);
                    setCurrentStartnr(selectedSequences[0].firstStartNumber);
                  }
                }}
              >
                {selectedDiscipline.startNumberSequences.map((seq) => (
                  <option
                    key={startnumberSequenceToString(seq)}
                    value={startnumberSequenceToString(seq)}
                  >
                    {startnumberSequenceToString(seq)}
                  </option>
                ))}
              </Select>
            </Box>
          )}
      </HStack>

      {selectedSequence && (
        <VStack>
          <HStack spacing={4} marginBottom={"20px"}>
            <Box width={"30%"}>
              <Text fontSize={"24px"}>Från:</Text>
              <Input
                type="text"
                fontSize={"24px"}
                bg="gray.200"
                readOnly
                value={selectedSequence?.firstStartNumber}
              />
            </Box>
            <Box width={"40%"}>
              <Text fontSize={"24px"}>Aktuell:</Text>
              <Input
                type="text"
                fontSize={"24px"}
                bg="yellow.200"
                value={currentStartnr || ""}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  const min = selectedSequence?.firstStartNumber || 0;
                  const max = selectedSequence?.lastStartNumber || -1;

                  if (!isNaN(value)) {
                    if (value >= min && value <= max) {
                      setCurrentStartnr(value);
                      setError("");
                    } else {
                      setError("Ange ett värde mellan från och till!");
                      setCurrentStartnr(value);
                    }
                  } else {
                    setCurrentStartnr(value);
                    setError("Ange ett giltigt nummer!");
                  }
                }}
              />
              {error && <Text color="red.500">{error}</Text>}
            </Box>
            <Box width={"30%"}>
              <Text fontSize={"24px"}>Till:</Text>
              <Input
                type="text"
                fontSize={"24px"}
                bg="gray.200"
                readOnly
                value={selectedSequence?.lastStartNumber}
              />
            </Box>
          </HStack>

          <HStack spacing={4} marginBottom={"20px"}>
            <Button
              colorScheme="green"
              onClick={startFetching}
              isDisabled={isFetching}
            >
              Starta
            </Button>
            <Button
              colorScheme="red"
              onClick={stopFetching}
              isDisabled={!isFetching}
            >
              Stoppa
            </Button>
          </HStack>

          <Box
            padding={"10px"}
            borderRadius={"10px"}
            borderColor={"black"}
            borderWidth={"1px"}
            shadow={"lg"}
            marginTop={"30px"}
            marginBottom={"30px"}
            hidden={rfidTagService.startnumber.detectedTags.length === 0}
          >
            <DetectedTagsTable
              detectedTags={rfidTagService.startnumber.detectedTags}
              detectedConflicts={rfidTagService.db.allTags.filter((e) =>
                rfidTagService.startnumber.detectedConflicts.includes(e.rfidtag)
              )}
              detectedAlreadyPersisted={rfidTagService.db.allTags.filter((e) =>
                rfidTagService.startnumber.detectedAlreadyPersistedTags.includes(
                  e.rfidtag
                )
              )}
              requiredAntennas={rfidTagService.db.allAntennas}
              requiredNumberOfAntennas={numberOfAntennasInUse}
              forgetDetectedTag={rfidTagService.functions.forgetDetectedTag}
              targetNumberOfTags={tagsPerStartnumber}
              currentStartnumber={currentStartnr}
            />
          </Box>

          <FastTable
            headers={["ID", "Tag", "Startnumber", "", ""]}
            data={rfidTagService.db.allTags}
            pageHeader={`Nummerlappar till ${globalContext.competition?.tag ?? "Ingen tävling vald"} ${new Date().toISOString().split('T')[0]}`}
            repeatHeaderRows={3}
            downloadFileName={`nummerlappar_${globalContext.competition?.tag ?? "Ingen tävling vald"}_${new Date().toISOString().split('T')[0]}`}

            rowFormat={"1fr 2fr 5fr 1fr 1fr"}
            toRowData={(p) => {
              return [`${p.id}`, `${p.rfidtag}`, `${p.startnumber}`];
            }}
            modifiableFields={
              ["startnumber"] as Array<keyof StartnumberRfidTag>
            }
            modifiableFieldsDisplayName={["Startnumber"]}
            fixedFields={["id", "rfidtag"]}
            fixedFieldsDisplayName={["ID", "rfidTag"]}
            onChange={async (updatedRow, oldRow) => {
              rfidTagService.functions.put(
                oldRow.id,
                updatedRow.rfidtag,
                updatedRow.startnumber
              );
              return true;
            }}
            onDelete={async (row) => {
              rfidTagService.functions.delete(row.rfidtag);
              return true;
            }}
          />
        </VStack>
      )}
    </div>
  );
}

function startnumberSequenceToString(seq: StartNumberSequence | null) {
  return `${seq?.firstStartNumber}-${seq?.lastStartNumber}`;
}

function DetectedTagsTable(props: {
  detectedTags: Array<{ tag: string; antennas: Set<string> }>;
  detectedConflicts: StartnumberRfidTag[];
  detectedAlreadyPersisted: StartnumberRfidTag[];
  requiredAntennas: Set<string>;
  requiredNumberOfAntennas: number;
  forgetDetectedTag: (tag: string) => void;
  targetNumberOfTags: number;
  currentStartnumber: number | null;
}) {
  const rowData = props.detectedTags.map((t) => {
    const matchingConflicts = props.detectedConflicts.filter(
      (c) => c.rfidtag === t.tag
    );
    const matchingAlreadyPersisted = props.detectedAlreadyPersisted.filter(
      (c) => c.rfidtag === t.tag
    );
    if (matchingConflicts.length > 0) {
      return {
        id: matchingConflicts[0].id,
        startnumber: matchingConflicts[0].startnumber,
        tag: t.tag,
        antennas: t.antennas,
        isConflict: true,
        isPersistedInDb: true,
      };
    } else if (matchingAlreadyPersisted.length > 0) {
      return {
        id: matchingAlreadyPersisted[0].id,
        startnumber: matchingAlreadyPersisted[0].startnumber,
        tag: t.tag,
        antennas: t.antennas,
        isConflict: false,
        isPersistedInDb: true,
      };
    } else {
      return {
        id: "-",
        startnumber: props.currentStartnumber,
        tag: t.tag,
        antennas: t.antennas,
        isConflict: false,
        isPersistedInDb: false,
      };
    }
  });

  const status = getTagCollectionStatus(
    props.detectedTags,
    props.detectedConflicts.map((c) => c.rfidtag),
    props.requiredAntennas,
    props.targetNumberOfTags,
    props.requiredNumberOfAntennas
  );

  return (
    <VStack>
      <Center>
        <Text
          padding={"20px"}
          margin={"20px"}
          hidden={status.status === TagCollectionStatus.NO_TAGS_DETECTED}
          background={
            status.status === TagCollectionStatus.TAG_COLLECTION_COMPLETE
              ? undefined
              : "red"
          }
          borderRadius={"10px"}
          textColor={
            status.status === TagCollectionStatus.TAG_COLLECTION_COMPLETE
              ? undefined
              : "white"
          }
          whiteSpace="pre-line"
          textAlign={"center"}
        >
          {status.description}
        </Text>
      </Center>
      <TableContainer>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Startnumber</Th>
              <Th>RfidTag</Th>
              <Th>Antennas</Th>
              <Th>Action</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rowData.map((r) => {
              return (
                <Tr
                  key={`foundrfidtagrow_${r.tag}${r.id}`}
                  background={
                    r.isConflict
                      ? "red"
                      : r.isPersistedInDb
                      ? "green"
                      : undefined
                  }
                >
                  <Td>{r.id}</Td>
                  <Td>{r.startnumber}</Td>
                  <Td>{r.tag}</Td>
                  <Td>
                    <HStack>
                      {[...props.requiredAntennas].map((a) => (
                        <Tag
                          colorScheme={r.antennas.has(a) ? "green" : "red"}
                          key={`${r.tag}:${a}`}
                        >
                          {a}
                        </Tag>
                      ))}
                    </HStack>
                  </Td>
                  <Td>
                    <Button
                      colorScheme="pink"
                      onClick={() => props.forgetDetectedTag(r.tag)}
                    >
                      Glöm
                    </Button>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </VStack>
  );
}

enum TagCollectionStatus {
  TAG_CONFLICT = 0,
  NO_TAGS_DETECTED = 1,
  TOO_MANY_TAGS = 2,
  TOO_FEW_TAGS = 3,
  MISSING_ANTENNA_OBSERVATIONS = 4,
  TAG_COLLECTION_COMPLETE = 5,
}

function getTagCollectionStatus(
  detectedTags: Array<{ tag: string; antennas: Set<string> }>,
  detectedConflicts: string[],
  requiredAntennas: Set<string>,
  targetNumberOfTags: number,
  numberOfAntennasInUse: number
) {
  // prettier-ignore
  const status = detectedConflicts.length > 0 
    ? TagCollectionStatus.TAG_CONFLICT
    : detectedTags.length === 0 && targetNumberOfTags > 0
    ? TagCollectionStatus.NO_TAGS_DETECTED
    : detectedTags.length > targetNumberOfTags
    ? TagCollectionStatus.TOO_MANY_TAGS
    : detectedTags.length < targetNumberOfTags
    ? TagCollectionStatus.TOO_FEW_TAGS
    : numberOfAntennasInUse > requiredAntennas.size || detectedTags.flatMap(t => [...requiredAntennas].map(a => t.antennas.has(a))).includes(false)
    ? TagCollectionStatus.MISSING_ANTENNA_OBSERVATIONS
    : TagCollectionStatus.TAG_COLLECTION_COMPLETE;

  // prettier-ignore
  switch(status) {
    case TagCollectionStatus.TAG_CONFLICT:
      return {status: status, description: `Konflikt: Följande taggar används för annat startnumber \n ${detectedConflicts.join(", ")}`};
    case TagCollectionStatus.NO_TAGS_DETECTED:
      return {status: status, description: "Inga taggar har hittats..."};
    case TagCollectionStatus.TOO_MANY_TAGS:
      return {status: status, description: `För många taggar: Endast ${targetNumberOfTags} taggar per nummerlapp`};
    case TagCollectionStatus.TOO_FEW_TAGS:
      return {status: status, description: `Väntar ännu på fler taggar för nummerlappen (saknar ${targetNumberOfTags - detectedTags.length} st)`};
    case TagCollectionStatus.MISSING_ANTENNA_OBSERVATIONS:
      return {status: status, description: "Antenn saknas: Taggarna måste ses av alla antenner."};
    case TagCollectionStatus.TAG_COLLECTION_COMPLETE:
      return {status: status, description: `${detectedTags.length} taggar har hittats och verifierats`};
  }
}

export default RegisterBibs;
